// src/mobile/MobileResourcesPage.js
// Mobile Resources page - Premium touch-optimized experience

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  X,
  Sparkles,
  ArrowUpDown,
  FileSearch,
  RefreshCw
} from 'lucide-react';
import MobileNavbar from './MobileNavbar';
import MobileResourceCard from './MobileResourceCard';
import MobileFooter from './MobileFooter';
import MobileBottomNav from './MobileBottomNav';
import { useResourcesList } from '../hooks/useContentful';
import { useResourceFilters, CONTENT_TYPES, SORT_OPTIONS } from '../hooks/useResourceFilters';
import './styles/mobile-resources.css';

// Content type tabs configuration
const TABS = [
  { id: CONTENT_TYPES.ALL, label: 'All' },
  { id: CONTENT_TYPES.ARTICLES, label: 'Articles' },
  { id: CONTENT_TYPES.CASE_STUDIES, label: 'Case Studies' },
  { id: CONTENT_TYPES.NEWS, label: 'News' },
];

// Sort options
const SORT_OPTIONS_LIST = [
  { id: SORT_OPTIONS.NEWEST, label: 'Newest' },
  { id: SORT_OPTIONS.OLDEST, label: 'Oldest' },
  { id: SORT_OPTIONS.FEATURED, label: 'Featured' },
];

const MobileResourcesPage = () => {
  const { data, loading, error, refetch } = useResourcesList();
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const filtersRef = useRef(null);
  const sentinelRef = useRef(null);

  // Detect when filters become sticky
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px 0px 0px 0px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const {
    filters,
    setSearch,
    setContentType,
    setSortOrder,
    toggleTopic,
    clearFilters,
    filteredData,
    availableTopics,
    countsByType,
    hasActiveFilters,
    filteredCount,
  } = useResourceFilters(data?.resources || []);

  // Handle search input
  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
  }, [setSearch]);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearch('');
  }, [setSearch]);

  // Handle tab change
  const handleTabChange = useCallback((tabId) => {
    setContentType(tabId);
  }, [setContentType]);

  // Handle sort change
  const handleSortChange = useCallback((sortId) => {
    setSortOrder(sortId);
    setShowSortMenu(false);
  }, [setSortOrder]);

  // Render loading skeleton
  const renderSkeleton = () => (
    <div className="mobile-resources__loading">
      {[1, 2, 3].map((i) => (
        <div key={i} className="mobile-resources__skeleton-card">
          <div className="mobile-resources__skeleton-image" />
          <div className="mobile-resources__skeleton-content">
            <div className="mobile-resources__skeleton-badge" />
            <div className="mobile-resources__skeleton-title" />
            <div className="mobile-resources__skeleton-title" />
            <div className="mobile-resources__skeleton-text" />
            <div className="mobile-resources__skeleton-text" />
          </div>
        </div>
      ))}
    </div>
  );

  // Render empty state
  const renderEmpty = () => (
    <motion.div
      className="mobile-resources__empty"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="mobile-resources__empty-icon">
        <FileSearch size={28} />
      </div>
      <h3 className="mobile-resources__empty-title">No results found</h3>
      <p className="mobile-resources__empty-text">
        Try adjusting your search or filters to find what you're looking for.
      </p>
      {hasActiveFilters && (
        <button
          className="mobile-resources__empty-btn"
          onClick={clearFilters}
        >
          <RefreshCw size={16} />
          Clear all filters
        </button>
      )}
    </motion.div>
  );

  // Render error state
  if (error) {
    return (
      <div className="mobile-resources">
        <MobileNavbar />
        <div className="mobile-resources__empty">
          <div className="mobile-resources__empty-icon">
            <X size={28} />
          </div>
          <h3 className="mobile-resources__empty-title">Unable to load resources</h3>
          <p className="mobile-resources__empty-text">
            Please check your connection and try again.
          </p>
          <button
            className="mobile-resources__empty-btn"
            onClick={refetch}
          >
            <RefreshCw size={16} />
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-resources">
      <MobileNavbar />

      {/* Hero Section */}
      <section className="mobile-resources__hero">
        <div className="mobile-resources__hero-bg">
          <div className="mobile-resources__hero-gradient" />
        </div>

        <motion.div
          className="mobile-resources__eyebrow"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Sparkles />
          <span>Knowledge Center</span>
        </motion.div>

        <motion.h1
          className="mobile-resources__title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Insights &
          <span className="mobile-resources__title-accent">Resources</span>
        </motion.h1>

        <motion.p
          className="mobile-resources__subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          AI-powered operations, manufacturing intelligence, and supply chain optimization insights.
        </motion.p>
      </section>

      {/* Sticky sentinel - triggers sticky state when scrolled past */}
      <div ref={sentinelRef} style={{ height: 1, marginTop: -1 }} />

      {/* Sticky Filter Bar */}
      <div
        ref={filtersRef}
        className={`mobile-resources__filters ${isSticky ? 'mobile-resources__filters--stuck' : ''}`}
      >
        {/* Search Bar */}
        <div className="mobile-resources__search">
          <div className="mobile-resources__search-wrapper">
            <Search size={18} className="mobile-resources__search-icon" />
            <input
              type="text"
              className="mobile-resources__search-input"
              placeholder="Search resources..."
              value={filters.search}
              onChange={handleSearchChange}
            />
            {filters.search && (
              <button
                className="mobile-resources__search-clear"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Content Type Tabs */}
        <div className="mobile-resources__tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`mobile-resources__tab ${
                filters.contentType === tab.id ? 'mobile-resources__tab--active' : ''
              }`}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
              <span className="mobile-resources__tab-count">
                {countsByType[tab.id] || 0}
              </span>
            </button>
          ))}
        </div>

        {/* Controls Bar */}
        <div className="mobile-resources__controls">
          <span className="mobile-resources__results-count">
            <strong>{filteredCount}</strong> {filteredCount === 1 ? 'result' : 'results'}
          </span>
          <div className="mobile-resources__actions">
            <button
              className={`mobile-resources__action-btn ${
                showSortMenu ? 'mobile-resources__action-btn--active' : ''
              }`}
              onClick={() => setShowSortMenu(!showSortMenu)}
            >
              <ArrowUpDown size={14} />
              {SORT_OPTIONS_LIST.find(s => s.id === filters.sortOrder)?.label || 'Sort'}
            </button>
          </div>
        </div>

        {/* Sort Menu Dropdown */}
        <AnimatePresence>
          {showSortMenu && (
            <motion.div
              className="mobile-resources__sort-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              style={{
                background: 'var(--bg-surface)',
                borderBottom: '1px solid var(--border-subtle)',
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: '12px 16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {SORT_OPTIONS_LIST.map((option) => (
                  <button
                    key={option.id}
                    className={`mobile-resources__tab ${
                      filters.sortOrder === option.id ? 'mobile-resources__tab--active' : ''
                    }`}
                    onClick={() => handleSortChange(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* End Sticky Filter Bar */}

      {/* Topic Filters */}
      {availableTopics.length > 0 && (
        <div className="mobile-resources__topics">
          <div className="mobile-resources__topics-header">
            <span className="mobile-resources__topics-label">Filter by topic</span>
            {filters.activeTopics.length > 0 && (
              <button
                className="mobile-resources__topics-clear"
                onClick={() => clearFilters()}
              >
                Clear ({filters.activeTopics.length})
              </button>
            )}
          </div>
          <div className="mobile-resources__topics-list">
            {availableTopics.map((topic) => (
              <button
                key={topic}
                className={`mobile-resources__topic-chip ${
                  filters.activeTopics.includes(topic)
                    ? 'mobile-resources__topic-chip--active'
                    : ''
                }`}
                onClick={() => toggleTopic(topic)}
              >
                {topic}
                {filters.activeTopics.includes(topic) && (
                  <span className="mobile-resources__topic-remove">
                    <X size={10} />
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Resource List */}
      {loading ? (
        renderSkeleton()
      ) : filteredData.length === 0 ? (
        renderEmpty()
      ) : (
        <div className="mobile-resources__list">
          {filteredData.map((resource, index) => (
            <MobileResourceCard
              key={resource.slug}
              resource={resource}
              index={index}
            />
          ))}
        </div>
      )}

      {/* Footer */}
      <MobileFooter />

      {/* Bottom Nav Spacer */}
      <div className="mobile-resources__nav-spacer" />

      {/* Bottom Navigation */}
      <MobileBottomNav alwaysVisible activeItem="resources" />
    </div>
  );
};

export default MobileResourcesPage;
